import React, { useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { BarChart2, ChevronUp, ChevronDown } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика накоплений и стоимости образования
 */
const SavingsChart = ({ savingsData, results }) => {
  // Пустые данные для предотвращения ошибок
  const emptyChartData = [];
  
  // Подготовка данных для графика, безопасно используем useMemo
  const chartData = useMemo(() => {
    if (!savingsData || savingsData.length === 0 || !results) {
      return emptyChartData;
    }
    
    // Получаем каждую третью точку для оптимизации отображения
    const simplifiedData = savingsData.filter((_, index) => index % 3 === 0 || index === savingsData.length - 1);
    
    // Добавляем линию стоимости образования
    return simplifiedData.map(item => ({
      ...item,
      month: item.month,
      year: Math.floor(item.month / 12),
      label: `${Math.floor(item.month / 12)} лет ${item.month % 12} мес.`,
      balance: Math.round(item.balance),
      contributions: Math.round(item.contribution),
      returns: Math.round(item.returns),
      goal: results.netEducationCost // Целевая сумма для обучения (статичная линия)
    }));
  }, [savingsData, results]);
  
  if (!savingsData || savingsData.length === 0 || !results) {
    return null;
  }

  // Расчет максимального значения для оси Y
  const maxValue = Math.max(
    Math.max(...chartData.map(item => item.balance), 0),
    results.netEducationCost * 1.1
  );

  // Форматирование подписей для оси X
  const formatXAxis = (value) => {
    return `${value} лет`;
  };

  // Форматирование подписей для оси Y
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)} млн`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)} тыс`;
    }
    return value;
  };

  // Настройка всплывающей подсказки
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">{`${Math.floor(payload[0].payload.month / 12)} лет ${payload[0].payload.month % 12} мес.`}</p>
          <div className="space-y-1">
            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {`Накопления: ${formatCurrency(payload[0].value)}`}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {`Взносы: ${formatCurrency(payload[0].payload.contributions)}`}
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              {`Доход от инвестиций: ${formatCurrency(payload[0].payload.returns)}`}
            </p>
            {payload[1] && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {`Стоимость образования: ${formatCurrency(payload[1].value)}`}
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-blue-500" />
          График накоплений на образование
        </h3>
      </div>

      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start gap-2">
          <div className="mt-0.5">
            {results.isEnough 
              ? <ChevronUp className="w-5 h-5 text-green-500" /> 
              : <ChevronDown className="w-5 h-5 text-red-500" />}
          </div>
          <div>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {results.isEnough
                ? `Ваши накопления превысят стоимость образования за ${Math.ceil(chartData.findIndex(d => d.balance >= results.netEducationCost) / 4)} года`
                : `По текущему плану накоплений, к началу обучения вам будет не хватать ${formatCurrency(Math.abs(results.savingsGap))}`}
            </p>
          </div>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="year" 
              tickFormatter={formatXAxis}
              axisLine={{ stroke: '#E5E7EB' }}
              tick={{ fill: '#6B7280' }}
            />
            <YAxis 
              tickFormatter={formatYAxis}
              axisLine={{ stroke: '#E5E7EB' }}
              tick={{ fill: '#6B7280' }}
              domain={[0, maxValue]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {/* Линия стоимости образования */}
            <ReferenceLine 
              y={results.netEducationCost} 
              stroke="#EF4444" 
              strokeDasharray="5 5"
              label={{ 
                value: 'Стоимость обучения', 
                position: 'bottom', 
                fill: '#EF4444',
                fontSize: 11
              }} 
            />
            
            {/* Линия с балансом накоплений */}
            <Line 
              type="monotone" 
              dataKey="balance" 
              name="Накопления" 
              stroke="#3B82F6" 
              strokeWidth={2}
              dot={{ stroke: '#1E40AF', strokeWidth: 1, r: 2 }}
              activeDot={{ stroke: '#1E40AF', strokeWidth: 1, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-8 mb-4">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-2">
          Структура накоплений
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Соотношение ваших взносов и дохода от инвестиций в итоговой сумме
        </p>
        
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[{
                name: 'Структура накоплений',
                'Текущие накопления': results.currentSavings,
                'Ваши взносы': results.totalContributions,
                'Инвестиционный доход': results.expectedReturns
              }]}
              margin={{ top: 5, right: 30, left: 30, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="name" hide />
              <YAxis 
                tickFormatter={formatYAxis}
                axisLine={{ stroke: '#E5E7EB' }}
                tick={{ fill: '#6B7280' }}
              />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend verticalAlign="top" />
              <Bar dataKey="Текущие накопления" fill="#60A5FA" />
              <Bar dataKey="Ваши взносы" fill="#818CF8" />
              <Bar dataKey="Инвестиционный доход" fill="#34D399" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          * Расчет основан на предположении о постоянной доходности инвестиций и уровне инфляции. 
          Фактические результаты могут отличаться в зависимости от рыночных условий.
        </p>
      </div>
    </div>
  );
};

export default SavingsChart;